import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Radio, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page, ShareStatisticsVo } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps,
    Button as ZhFilterButton,
    Input as ZhFilterInput
} from "zhihao-ui-react";

import type { ShareStatisticsQueryParams } from "@/api/services/shareStatisticsService";
import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import {
    SHARE_OPERATION_RECORD_CLIENT_TYPE,
    SHARE_OPERATION_RECORD_OPERATION_ENTRY,
    SHARE_OPERATION_RECORD_OPERATOR_CARRIER,
    SHARE_OPERATION_RECORD_OPERATOR_METHOD,
    SHARE_OPERATION_RECORD_OPERATOR_ROLE,
    YES_OR_NO
} from "@zhihaoscm/meta";
import { TABS_TITLE_MAP } from "./utils";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

type ShareRow = Partial<ShareStatisticsVo> & {
    share?: Partial<ShareStatisticsVo["share"]>;
};

type ShareFilterState = Pick<
    ShareStatisticsQueryParams,
    "operatorRole" | "clientType" | "operatorMethod" | "operatorCarrier" | "isGenerateWaybill"
> & {
    operateTime?: [string, string] | null;
};

export interface ShareStatisticalPageProps {
    loadRows?: (query: ShareStatisticsQueryParams) => Promise<Page<ShareRow>>;
    exportRows?: (query: Omit<ShareStatisticsQueryParams, "page" | "size">, filename: string) => Promise<unknown>;
    permissions?: string[];
}

/** Vue 分享统计列表的 API adapter，保留旧接口路径和分页参数。 */
export async function loadShareStatistics(query: ShareStatisticsQueryParams): Promise<Page<ShareRow>> {
    const { shareStatisticsService } = await import("@/api/services/shareStatisticsService");
    return shareStatisticsService.paging(query) as Promise<Page<ShareRow>>;
}

/** Vue 分享统计导出 API adapter，保持原接口和文件名契约。 */
export async function exportShareStatistics(
    query: Omit<ShareStatisticsQueryParams, "page" | "size">,
    filename: string
): Promise<unknown> {
    const { shareStatisticsService } = await import("@/api/services/shareStatisticsService");
    return shareStatisticsService.exportShareStatistics(query, filename);
}

// 沿用旧 Vue ProTable/useTable 的默认分页大小，首次进入和重置均按每页 10 条请求。
const defaultQuery: ShareStatisticsQueryParams = { page: 1, size: 10, businessType: 1 };

const valueText = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));

const metaText = (value: unknown, getIntro: (code: number) => string | null | undefined) => {
    if (value == null || value === "") return DEFAULT_VALUE;
    return getIntro(Number(value)) || DEFAULT_VALUE;
};

const dateText = (value: unknown) => (value ? dateFormat(String(value), "YYYY-MM-DD HH:mm") : DEFAULT_VALUE);

const selectOptions = (options: Array<{ label: string; value: number | string }>) =>
    options.map(option => ({ label: option.label, value: Number(option.value) }));

/** 将旧 Vue 搜索栏的本地值转换为接口查询参数，日期按整日边界提交。 */
const toShareFilterQuery = (filters: ShareFilterState): Partial<ShareStatisticsQueryParams> => {
    const query: Partial<ShareStatisticsQueryParams> = {};
    const filterKeys: Array<keyof Omit<ShareFilterState, "operateTime">> = [
        "operatorRole",
        "clientType",
        "operatorMethod",
        "operatorCarrier",
        "isGenerateWaybill"
    ];

    filterKeys.forEach(key => {
        const value = filters[key];
        if (value !== undefined && value !== null) {
            query[key] = value;
        }
    });

    if (filters.operateTime?.[0] && filters.operateTime[1]) {
        query.beginTime = dateFormat(filters.operateTime[0], LOG_DATE_FORMAT_START);
        query.endTime = dateFormat(filters.operateTime[1], LOG_DATE_FORMAT_END);
    }

    return query;
};

/** 分享统计最小可用页面：需求/空船切换、筛选、分页和异常/空态。 */
export function ShareStatisticalPage({
    loadRows = loadShareStatistics,
    exportRows = exportShareStatistics,
    permissions
}: ShareStatisticalPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 保持旧 Vue v-auth 语义：导出入口只对指定按钮权限开放。
    const canExport = (permissions ?? storePermissions).includes("ROLE_SHARE_STATISTICS_EXPORT");
    const [businessType, setBusinessType] = useState<1 | 2>(1);
    const [businessId, setBusinessId] = useState("");
    const [filters, setFilters] = useState<ShareFilterState>({});
    const [query, setQuery] = useState<ShareStatisticsQueryParams>(defaultQuery);
    const [rows, setRows] = useState<ShareRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRows(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("分享统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRows, query]);

    const switchBusinessType = (value: 1 | 2) => {
        setBusinessType(value);
        setBusinessId("");
        setFilters({});
        setQuery({ page: 1, size: 10, businessType: value });
    };

    const submit = () => {
        setQuery({
            page: 1,
            size: 10,
            businessType,
            ...(businessId.trim() ? { businessId: businessId.trim() } : {}),
            ...toShareFilterQuery(filters)
        });
    };

    const reset = () => {
        setBusinessId("");
        setFilters({});
        setQuery({ page: 1, size: 10, businessType });
    };

    const setFilter = <K extends keyof ShareFilterState>(key: K, value: ShareFilterState[K]) => {
        setFilters(current => ({ ...current, [key]: value }));
    };

    // 延续 Vue ZhConfirm：确认后才去掉分页参数并发起当前筛选条件的导出请求。
    const confirmExport = async () => {
        const exportQuery = { ...query };
        delete exportQuery.page;
        delete exportQuery.size;
        setExporting(true);
        try {
            await exportRows(exportQuery, businessType === 1 ? "需求分享统计" : "空船分享统计");
            setExportOpen(false);
            notifyOperationSuccess("导出成功");
        } catch {
            setError("分享统计导出失败");
        } finally {
            setExporting(false);
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 分享统计 zh-table 的当前展示字段。
    const shareColumns = useMemo<TableColumnProps<ShareRow>[]>(
        () => [
            {
                key: "index",
                label: "编号",
                width: 78,
                prop: "index",
                render: ({ row, rowIndex }) => {
                    const index = (query.page! - 1) * query.size! + rowIndex + 1;
                    const trackId = row.share?.trackId;
                    return trackId ? (
                        <Link to={`/dashboard/shareStatistical/detail/${trackId}?tab=${businessType}`}>{index}</Link>
                    ) : (
                        index
                    );
                }
            },
            {
                key: "businessId",
                label: businessType === 1 ? "需求编号" : "空船编号",
                width: 162,
                prop: "share.businessId",
                render: ({ row }) => {
                    const id = row.share?.businessId;
                    const href =
                        businessType === 1
                            ? `/logistics/shippingNeed/platformShipNeedInfo/${id || ""}`
                            : `/logistics/emptyShipInformationDetail/${id || ""}`;
                    return id ? <Link to={href}>{valueText(id)}</Link> : valueText(id);
                }
            },
            {
                key: "operator",
                label: "分享用户",
                prop: "share.operator",
                render: ({ row }) => valueText(row.share?.operator)
            },
            {
                key: "operatorRole",
                label: "用户身份",
                prop: "share.operatorRole",
                render: ({ row }) =>
                    metaText(row.share?.operatorRole, code => SHARE_OPERATION_RECORD_OPERATOR_ROLE.getIntroByCode(code))
            },
            {
                key: "clientType",
                label: "分享端",
                prop: "share.clientType",
                render: ({ row }) =>
                    metaText(row.share?.clientType, code => SHARE_OPERATION_RECORD_CLIENT_TYPE.getIntroByCode(code))
            },
            {
                key: "operationEntry",
                label: "分享入口",
                prop: "share.operationEntry",
                render: ({ row }) =>
                    metaText(row.share?.operationEntry, code =>
                        SHARE_OPERATION_RECORD_OPERATION_ENTRY.getIntroByCode(code)
                    )
            },
            {
                key: "operatorMethod",
                label: "分享方式",
                prop: "share.operatorMethod",
                render: ({ row }) =>
                    metaText(row.share?.operatorMethod, code =>
                        SHARE_OPERATION_RECORD_OPERATOR_METHOD.getIntroByCode(code)
                    )
            },
            {
                key: "operatorCarrier",
                label: "分享载体",
                prop: "share.operatorCarrier",
                render: ({ row }) =>
                    metaText(row.share?.operatorCarrier, code =>
                        SHARE_OPERATION_RECORD_OPERATOR_CARRIER.getIntroByCode(code)
                    )
            },
            {
                key: "operateTime",
                label: "操作时间",
                width: 188,
                prop: "share.operateTime",
                render: ({ row }) => dateText(row.share?.operateTime)
            },
            {
                key: "miniAppVisitorCount",
                label: "小程序访问人数",
                width: 148,
                prop: "miniAppVisitorCount",
                sortable: "custom",
                render: ({ row }) => valueText(row.miniAppVisitorCount)
            },
            ...(businessType === 1
                ? [
                      {
                          key: "viewRequirementNumber",
                          label: "查看需求人数",
                          width: 134,
                          prop: "viewRequirementNumber",
                          sortable: "custom" as const,
                          render: ({ row }: { row: ShareRow }) => valueText(row.viewRequirementNumber)
                      },
                      {
                          key: "viewRequirementCount",
                          label: "查看需求次数",
                          width: 134,
                          prop: "viewRequirementCount",
                          sortable: "custom" as const,
                          render: ({ row }: { row: ShareRow }) => valueText(row.viewRequirementCount)
                      },
                      {
                          key: "acceptCount",
                          label: "报价人数",
                          width: 106,
                          prop: "acceptCount",
                          sortable: "custom" as const,
                          render: ({ row }: { row: ShareRow }) => valueText(row.acceptCount)
                      },
                      {
                          key: "isGenerateWaybill",
                          label: "是否生成船运单",
                          width: 148,
                          prop: "isGenerateWaybill",
                          render: ({ row }: { row: ShareRow }) =>
                              metaText(row.isGenerateWaybill, code => YES_OR_NO.getIntroByCode(code))
                      }
                  ]
                : [
                      {
                          key: "viewEmptyShipNumber",
                          label: "查看空船人数",
                          width: 134,
                          prop: "viewEmptyShipNumber",
                          sortable: "custom" as const,
                          render: ({ row }: { row: ShareRow }) => valueText(row.viewEmptyShipNumber)
                      },
                      {
                          key: "viewEmptyShipCount",
                          label: "查看空船次数",
                          width: 134,
                          prop: "viewEmptyShipCount",
                          sortable: "custom" as const,
                          render: ({ row }: { row: ShareRow }) => valueText(row.viewEmptyShipCount)
                      }
                  ])
        ],
        [businessType, query.page, query.size]
    );

    return (
        <section aria-label="share-statistical-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space direction="vertical" className="w-full">
                    <h2 className="m-0">{businessType === 1 ? "需求分享统计" : "空船分享统计"}</h2>
                    <div className="text-xs leading-4 text-black/40">{TABS_TITLE_MAP[businessType].sub}</div>
                    <Radio.Group
                        aria-label="分享类型"
                        value={businessType}
                        onChange={event => switchBusinessType(event.target.value as 1 | 2)}
                    >
                        <Radio.Button value={1}>需求分享</Radio.Button>
                        <Radio.Button value={2}>空船分享</Radio.Button>
                    </Radio.Group>
                    {/* 复用列表查询栏契约，保证日期范围统一使用 Vue 的 220px 控件规格。 */}
                    <Space wrap className="admin-table-filter-form">
                        <ZhFilterInput
                            aria-label="需求或空船编号"
                            placeholder={businessType === 1 ? "请输入需求编号搜索" : "请输入空船编号搜索"}
                            value={businessId}
                            onChange={event => setBusinessId(event.target.value)}
                        />
                        <ZhSelect
                            aria-label="用户身份"
                            allowClear
                            placeholder="请选择用户身份"
                            value={filters.operatorRole}
                            options={selectOptions(SHARE_OPERATION_RECORD_OPERATOR_ROLE.options)}
                            onChange={value => setFilter("operatorRole", value as number | undefined)}
                        />
                        <ZhSelect
                            aria-label="分享端"
                            allowClear
                            placeholder="请选择分享端"
                            value={filters.clientType}
                            options={selectOptions(SHARE_OPERATION_RECORD_CLIENT_TYPE.options)}
                            onChange={value => setFilter("clientType", value as number | undefined)}
                        />
                        <ZhSelect
                            aria-label="分享方式"
                            allowClear
                            placeholder="请选择分享方式"
                            value={filters.operatorMethod}
                            options={selectOptions(SHARE_OPERATION_RECORD_OPERATOR_METHOD.options)}
                            onChange={value => setFilter("operatorMethod", value as number | undefined)}
                        />
                        <ZhSelect
                            aria-label="分享载体"
                            allowClear
                            placeholder="请选择分享载体"
                            value={filters.operatorCarrier}
                            options={selectOptions(SHARE_OPERATION_RECORD_OPERATOR_CARRIER.options)}
                            onChange={value => setFilter("operatorCarrier", value as number | undefined)}
                        />
                        <ZhDateRangePicker
                            aria-label="操作时间"
                            value={filters.operateTime}
                            valueFormat="YYYY-MM-DD"
                            format="YYYY-MM-DD"
                            rangeSeparator="-"
                            startPlaceholder="操作开始时间"
                            endPlaceholder="操作结束时间"
                            onChange={(_value, formattedValue) => setFilter("operateTime", formattedValue)}
                        />
                        {businessType === 1 ? (
                            <ZhSelect
                                aria-label="是否生成船运单"
                                allowClear
                                placeholder="请选择是否生成船运单"
                                value={filters.isGenerateWaybill}
                                options={selectOptions(YES_OR_NO.options)}
                                onChange={value => setFilter("isGenerateWaybill", value as number | undefined)}
                            />
                        ) : null}
                        <ZhFilterButton type="primary" aria-label="查询" onClick={submit}>
                            查询
                        </ZhFilterButton>
                        <ZhFilterButton aria-label="重置" onClick={reset}>
                            重置
                        </ZhFilterButton>
                    </Space>
                    <div className="text-sm text-black/40">
                        注：用户身份指的是用户分享时的身份，若后续进行切换，不会更新
                    </div>
                </Space>
            </Card>
            <Card
                title={businessType === 1 ? "需求分享统计" : "空船分享统计"}
                extra={
                    canExport ? (
                        <Button
                            type="primary"
                            aria-label="导出分享统计"
                            loading={exporting}
                            onClick={() => setExportOpen(true)}
                        >
                            导出
                        </Button>
                    ) : null
                }
            >
                <ZhTable<ShareRow>
                    rowKey={row => String(row.share?.businessId || row.share?.operator || "share-row")}
                    data={rows}
                    columns={shareColumns}
                    pagination={false}
                    onSortChange={({ prop, order }) => {
                        if (!prop) return;
                        setQuery(current => ({
                            ...current,
                            page: 1,
                            ...(order
                                ? { sortKey: prop, sortOrder: order === "ascending" ? "asc" : "desc" }
                                : { sortKey: undefined, sortOrder: undefined })
                        }));
                    }}
                    onHeaderClick={column => {
                        // 该列同时存在筛选控件和表头文案，使用表头点击保留 Vue 的自定义排序契约，避免 aria-label 冲突。
                        if (column.prop !== "isGenerateWaybill") return;
                        const isAscending = query.sortKey === column.prop && query.sortOrder === "asc";
                        setQuery(current => ({
                            ...current,
                            page: 1,
                            sortKey: column.prop,
                            sortOrder: isAscending ? "desc" : "asc"
                        }));
                    }}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {error ? <Alert role="alert" type="error" message={error} /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 对齐 Vue ProTable：切换每页条数回到第一页，翻页时保留当前条数。
                                        page: pageSize === current.size ? page : 1,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={exportOpen}
                title="导出"
                width={400}
                closeOnClickModal={false}
                onCancel={() => setExportOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setExportOpen(false)}>取消</Button>
                        <Button type="primary" loading={exporting} onClick={() => void confirmExport()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                是否导出当前数据？
            </ZhDialog>
        </section>
    );
}
